/**
 * Centralized, typed access to Vite env vars. Import `env` — never read
 * `import.meta.env.VITE_*` directly in feature code.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Copy .env.example to .env.local and fill it in.`,
    );
  }
  return value;
}

function optional(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

const raw = import.meta.env;

function requiredAbsoluteHttpUrl(name: string, value: string | undefined): string {
  const resolved = required(name, value).trim();
  if (!/^https?:\/\//i.test(resolved)) {
    throw new Error(`${name} must be an absolute http(s) URL, got "${resolved}"`);
  }
  return resolved.replace(/\/+$/, '');
}

export const env = {
  mode: raw.MODE as 'development' | 'production' | 'test',
  isDev: raw.DEV,
  isProd: raw.PROD,

  /** REST API base (absolute URL, no trailing slash). */
  apiBaseUrl: requiredAbsoluteHttpUrl('VITE_API_BASE_URL', raw.VITE_API_BASE_URL),
  wsUrl: required('VITE_WS_URL', raw.VITE_WS_URL),
  mapboxToken: optional(raw.VITE_MAPBOX_TOKEN),
  sentryDsn: optional(raw.VITE_SENTRY_DSN),
  featureFlagsKey: optional(raw.VITE_FEATURE_FLAGS_KEY),

  /** `POST …/auth/super-admin/login` */
  authSuperAdminLoginPath:
    optional(raw.VITE_AUTH_SUPER_ADMIN_LOGIN_PATH) ?? '/auth/super-admin/login',

  /** `GET` current user — optional; set `VITE_AUTH_PROFILE_ENABLED=false` if not deployed yet. */
  authProfileEnabled: raw.VITE_AUTH_PROFILE_ENABLED !== 'false',
  authProfilePath: optional(raw.VITE_AUTH_PROFILE_PATH) ?? '/auth/me',

  /** `POST` refresh session */
  authRefreshPath: optional(raw.VITE_AUTH_REFRESH_PATH) ?? '/auth/refresh',

  /** `POST` logout */
  authLogoutPath: optional(raw.VITE_AUTH_LOGOUT_PATH) ?? '/auth/logout',

  /** `GET` catalog brands (admin) */
  adminCatalogBrandsPath: optional(raw.VITE_ADMIN_CATALOG_BRANDS_PATH) ?? '/admin/catalog/brands',

  /** `POST` catalog categories (admin) */
  adminCatalogCategoriesPath:
    optional(raw.VITE_ADMIN_CATALOG_CATEGORIES_PATH) ?? '/admin/catalog/categories',

  /**
   * `GET` master products catalog list (admin) —
   * gateway `GET /v1/admin/catalog/master-products/catalog`.
   */
  adminCatalogMasterProductsCatalogPath:
    optional(raw.VITE_ADMIN_CATALOG_MASTER_PRODUCTS_CATALOG_PATH) ??
    '/admin/catalog/master-products/catalog',

  /** Variant CRUD, detail, barcodes (list/add) — gateway `/v1/admin/catalog/products` */
  adminCatalogProductsPath:
    optional(raw.VITE_ADMIN_CATALOG_PRODUCTS_PATH) ?? '/admin/catalog/products',

  /**
   * Store-scoped product search — gateway `GET /v1/admin/products/search`.
   * Requires `storeId`; optional `query`, `inStockOnly`, `page`, `limit`.
   */
  adminProductsSearchPath:
    optional(raw.VITE_ADMIN_PRODUCTS_SEARCH_PATH) ?? '/admin/products/search',

  /** Delete barcode by id — gateway `DELETE /v1/admin/catalog/barcodes/:id` */
  adminCatalogBarcodesPath:
    optional(raw.VITE_ADMIN_CATALOG_BARCODES_PATH) ?? '/admin/catalog/barcodes',

  /**
   * Backend proxy for FastGST HSN tax lookup.
   * The FastGST API key must remain on the server and must never be exposed as a VITE variable.
   */
  adminCatalogHsnTaxPath:
    optional(raw.VITE_ADMIN_CATALOG_HSN_TAX_PATH) ?? '/admin/catalog/hsn',

  /** `POST` master products (admin) — gateway `POST /v1/admin/catalog/master-products` */
  adminCatalogMasterProductsPath:
    optional(raw.VITE_ADMIN_CATALOG_MASTER_PRODUCTS_PATH) ?? '/admin/catalog/master-products',

  /** Admin catalog kits — `GET/POST/PUT …/v1/admin/kits` */
  adminCatalogKitsPath: optional(raw.VITE_ADMIN_CATALOG_KITS_PATH) ?? '/admin/kits',

  /** Admin home banners — `GET/POST/PATCH/DELETE …/v1/admin/banners` */
  adminBannersPath: optional(raw.VITE_ADMIN_BANNERS_PATH) ?? '/admin/banners',
  /** Admin catalog quarantine list — gateway `GET /v1/admin/catelog/quarantine` (note: backend path uses `catelog`). */
  adminCatalogQuarantinePath:
    optional(raw.VITE_ADMIN_CATELOG_QUARANTINE_PATH) ?? '/admin/catalog/quarantine',

  /**
   * Admin darkstores CRUD — gateway `GET/POST/PUT/PATCH …/v1/admin/darkstores`.
   */
  adminDarkstoresPath:
    optional(raw.VITE_ADMIN_DARKSTORES_PATH) ??
    optional(raw.VITE_INVENTORY_DARKSTORES_LIST_PATH) ??
    '/admin/darkstores',

  /**
   * Admin darkstore list — `GET {apiBaseUrl}{path}` (gateway `GET /v1/admin/darkstores`).
   * @deprecated Prefer `adminDarkstoresPath` for new code.
   */
  inventoryDarkstoresListPath:
    optional(raw.VITE_INVENTORY_DARKSTORES_LIST_PATH) ??
    optional(raw.VITE_INVENTORY_DARKSTORES_BASE_PATH) ??
    '/admin/darkstores',

  /**
   * WMS store inventory — `GET {apiBaseUrl}{path}/:storeId/inventory`
   * (gateway `GET /v1/admin/wms/stores/:storeId/inventory`).
   */
  inventoryWmsStoresBasePath:
    optional(raw.VITE_INVENTORY_WMS_STORES_BASE_PATH) ?? '/admin/wms/stores',

  /**
   * Live sellable stock — `GET {apiBaseUrl}{path}/stores/:storeId/products`
   * (gateway `GET /v1/admin/live-inventory/stores/:storeId/products`).
   */
  adminLiveInventoryBasePath:
    optional(raw.VITE_ADMIN_LIVE_INVENTORY_BASE_PATH) ?? '/admin/live-inventory',

  /** Admin WMS — receiving, bins, stock (`GET/POST …/v1/admin/wms/...`). */
  adminWmsBasePath: optional(raw.VITE_ADMIN_WMS_BASE_PATH) ?? '/admin/wms',

  /** Admin warehouse staff — `GET/POST/PUT …/v1/admin/employees`. */
  adminEmployeesPath: optional(raw.VITE_ADMIN_EMPLOYEES_PATH) ?? '/admin/employees',

  /** Admin delivery agents — `GET/POST/PUT …/v1/admin/delivery-agents`. */
  adminDeliveryAgentsPath:
    optional(raw.VITE_ADMIN_DELIVERY_AGENTS_PATH) ?? '/admin/delivery-agents',

  /** Admin rider ops — `GET …/v1/admin/riders/...` (live sessions, dispatch). */
  adminRidersPath: optional(raw.VITE_ADMIN_RIDERS_PATH) ?? '/admin/riders',

  /** Admin profiles (customers, admins) — base `GET …/v1/admin/profiles`. */
  adminProfilesPath: optional(raw.VITE_ADMIN_PROFILES_PATH) ?? '/admin/profiles',

  /** Ops fulfilment KPIs — `GET …/v1/admin/dashboard` (not the business home). */
  adminDashboardPath: optional(raw.VITE_ADMIN_DASHBOARD_PATH) ?? '/admin/dashboard',

  /** Admin business home — `GET …/v1/admin/dashboard/business`. */
  adminBusinessDashboardPath:
    optional(raw.VITE_ADMIN_BUSINESS_DASHBOARD_PATH) ?? '/admin/dashboard/business',

  /**
   * Darkstore order dashboard — `GET …/v1/admin/dashboard/warehouse/{comparison|peak-hours|top-customers}`.
   * Not the older `GET /v1/admin/dashboard` KPI endpoint.
   */
  adminWarehouseDashboardPath:
    optional(raw.VITE_ADMIN_WAREHOUSE_DASHBOARD_PATH) ?? '/admin/dashboard/warehouse',

  /** Admin coupons CRUD — `GET/POST/PATCH/DELETE …/v1/admin/coupons`. */
  adminCouponsPath: optional(raw.VITE_ADMIN_COUPONS_PATH) ?? '/admin/coupons',

  /** Admin delivery fee tiers — `GET/PUT …/v1/admin/delivery-charges`. */
  adminDeliveryChargesPath:
    optional(raw.VITE_ADMIN_DELIVERY_CHARGES_PATH) ?? '/admin/delivery-charges',

  /**
   * Staff in-store cash sales — `GET/POST …/v1/staff/offline-orders`.
   * Cart and search money is rupees; checkout `amountPaisa` is paise.
   */
  staffOfflineOrdersPath: optional(raw.VITE_STAFF_OFFLINE_ORDERS_PATH) ?? '/staff/offline-orders',
} as const;

export type AppEnv = typeof env;
